import Popup from "@/shared/components/layers/Popup/Popup";
import { Button } from "@/shared/components/common/Button/Button";
import { Input } from "@/shared/components/common/Input/Input";
import { Select } from "@/shared/components/common/Select/Select";
import { MONTH } from "@/shared/model/constants";
import css from "./CreateDepartmentForecastPopup.module.css";
import useCreateDepartmentForecastPopup from "./useCreateDepartmentForecastPopup";

type CreateDepartmentForecastPopupProps = {
  open: boolean;
  onClose: () => void;
};

function formatCurrency(value: number) {
  return `${value.toLocaleString("ru-RU")} ₽`;
}

function formatDelta(value: number) {
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${value.toLocaleString("ru-RU")} ₽`;
}

function getDeltaClassName(value: number, css: Record<string, string>) {
  if (value > 0) return css.deltaPositive;
  if (value < 0) return css.deltaNegative;
  return css.deltaNeutral;
}

function CreateDepartmentForecastPopup(props: CreateDepartmentForecastPopupProps) {
  const { open, onClose } = props;
  const {
    availableDepartments,
    currentSummary,
    departmentId,
    year,
    monthRows,
    loading,
    loadError,
    savingYear,
    savingMonth,
    departmentsLoading,
    departmentsError,
    hasDepartments,
    setDepartmentId,
    handleYearChange,
    handleMonthFieldChange,
    saveMonth,
    saveYear,
  } = useCreateDepartmentForecastPopup(open);

  return (
    <Popup open={open} onClose={onClose} title="Прогноз продаж отдела" size="xl" fullHeight>
      <div className={css.container}>
        <div className={css.controls}>
          <Select
            name="departmentId"
            label="Подразделение"
            value={departmentId ? String(departmentId) : ""}
            onChange={(value) => setDepartmentId(Number(value))}
            options={[
              { value: "", label: "Выберите подразделение" },
              ...availableDepartments.map((department) => ({
                value: String(department.id),
                label: department.name,
              })),
            ]}
            loading={departmentsLoading}
            error={departmentsError ? "Ошибка загрузки подразделений" : undefined}
            errorText="Ошибка загрузки подразделений"
            loadingText="Загрузка подразделений..."
            required
          />

          <div className={css.yearField}>
            <label className={css.fieldLabel}>Год</label>
            <Input
              type="number"
              min={2024}
              value={year}
              onChange={(event) => handleYearChange(event.target.value)}
            />
          </div>

          <div className={css.headerActions}>
            <Button variant="success" onClick={saveYear} disabled={!hasDepartments || loading || savingYear}>
              {savingYear ? "Сохраняем..." : "Сохранить год"}
            </Button>
          </div>
        </div>

        {loadError ? <div className={css.errorBanner}>{loadError}</div> : null}

        {!hasDepartments && !departmentsLoading ? (
          <div className={css.emptyState}>Нет доступных подразделений для постановки прогноза.</div>
        ) : (
          <>
            <div className={css.meta}>
              <div>
                Кварталы и итог года пересчитываются по сохранённым значениям.
              </div>
              {currentSummary?.departmentName ? (
                <div>
                  Текущий отдел: <span className={css.metaValue}>{currentSummary.departmentName}</span>
                </div>
              ) : null}
            </div>

            {loading ? <div className={css.status}>Загрузка сводки...</div> : null}

            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Месяц</th>
                    <th>Прогноз оборота</th>
                    <th>Прогноз валовой прибыли</th>
                    <th>План компании</th>
                    <th>Дельта к плану</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map((row) => (
                    <tr key={row.month}>
                      <td className={css.monthName}>{MONTH[row.month - 1]}</td>
                      <td>
                        <input
                          className={css.cellInput}
                          type="number"
                          min={0}
                          value={row.turnoverValue === 0 ? "" : row.turnoverValue}
                          placeholder="0"
                          onChange={(event) =>
                            handleMonthFieldChange(row.month, "turnoverValue", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className={css.cellInput}
                          type="number"
                          min={0}
                          value={row.grossProfitValue === 0 ? "" : row.grossProfitValue}
                          placeholder="0"
                          onChange={(event) =>
                            handleMonthFieldChange(row.month, "grossProfitValue", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <div className={css.metricCell}>
                          <span>Оборот: {formatCurrency(row.companyPlan.turnover)}</span>
                          <span>Валовая: {formatCurrency(row.companyPlan.grossProfit)}</span>
                        </div>
                      </td>
                      <td>
                        <div className={css.metricCell}>
                          <span
                            className={
                              row.comparison.turnoverDelta > 0
                                ? css.deltaPositive
                                : row.comparison.turnoverDelta < 0
                                  ? css.deltaNegative
                                  : css.deltaNeutral
                            }
                          >
                            Оборот: {formatDelta(row.comparison.turnoverDelta)}
                          </span>
                          <span
                            className={
                              row.comparison.grossProfitDelta > 0
                                ? css.deltaPositive
                                : row.comparison.grossProfitDelta < 0
                                  ? css.deltaNegative
                                  : css.deltaNeutral
                            }
                          >
                            Валовая: {formatDelta(row.comparison.grossProfitDelta)}
                          </span>
                        </div>
                      </td>
                      <td className={css.actionCell}>
                        <Button
                          variant="secondary"
                          size="s"
                          onClick={() => saveMonth(row.month)}
                          disabled={!row.isDirty || savingYear || savingMonth === row.month}
                        >
                          {savingMonth === row.month ? "..." : "Сохранить"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {currentSummary ? (
              <div className={css.summarySection}>
                <div className={css.summaryTitle}>Итоги по сохранённым значениям</div>
                <div className={css.summaryGrid}>
                  {[
                    { key: "Q1", label: "1 квартал", data: currentSummary.quarters.Q1 },
                    { key: "Q2", label: "2 квартал", data: currentSummary.quarters.Q2 },
                    { key: "Q3", label: "3 квартал", data: currentSummary.quarters.Q3 },
                    { key: "Q4", label: "4 квартал", data: currentSummary.quarters.Q4 },
                    { key: "year", label: "Итого за год", data: currentSummary.yearTotal },
                  ].map((item) => (
                    <div key={item.key} className={css.summaryCard}>
                      <div className={css.summaryCardHead}>
                        <div className={css.summaryCardTitle}>{item.label}</div>
                        <div className={css.summaryCardSubtitle}>Оборот и валовая прибыль</div>
                      </div>
                      <div className={css.summaryMetrics}>
                        <div className={css.summaryMetricBlock}>
                          <div className={css.summaryMetricLabel}>Прогноз</div>
                          <div className={css.summaryMetricItem}>
                            <span className={css.summaryMetricName}>Оборот</span>
                            <span className={css.summaryMetricValue}>{formatCurrency(item.data.forecast.turnover)}</span>
                          </div>
                          <div className={css.summaryMetricItem}>
                            <span className={css.summaryMetricName}>Валовая прибыль</span>
                            <span className={css.summaryMetricValue}>{formatCurrency(item.data.forecast.grossProfit)}</span>
                          </div>
                        </div>

                        <div className={css.summaryMetricBlock}>
                          <div className={css.summaryMetricLabel}>План компании</div>
                          <div className={css.summaryMetricItem}>
                            <span className={css.summaryMetricName}>Оборот</span>
                            <span className={css.summaryMetricValue}>{formatCurrency(item.data.companyPlan.turnover)}</span>
                          </div>
                          <div className={css.summaryMetricItem}>
                            <span className={css.summaryMetricName}>Валовая прибыль</span>
                            <span className={css.summaryMetricValue}>{formatCurrency(item.data.companyPlan.grossProfit)}</span>
                          </div>
                        </div>

                        <div className={css.summaryMetricBlock}>
                          <div className={css.summaryMetricLabel}>Дельта к плану</div>
                          <div className={css.summaryMetricItem}>
                            <span className={css.summaryMetricName}>Оборот</span>
                            <span className={`${css.summaryMetricValue} ${getDeltaClassName(item.data.comparison.turnoverDelta, css)}`}>
                              {formatDelta(item.data.comparison.turnoverDelta)}
                            </span>
                          </div>
                          <div className={css.summaryMetricItem}>
                            <span className={css.summaryMetricName}>Валовая прибыль</span>
                            <span className={`${css.summaryMetricValue} ${getDeltaClassName(item.data.comparison.grossProfitDelta, css)}`}>
                              {formatDelta(item.data.comparison.grossProfitDelta)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Popup>
  );
}

export default CreateDepartmentForecastPopup;
